import { mergeAttributes, Node } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion from '@tiptap/suggestion';

export const ItemPluginKey = new PluginKey('item');

export const Item = Node.create({
  name: 'item',

  priority: 102,

  addOptions() {
    return {
      HTMLAttributes: {},
      deleteTriggerWithBackspace: true,
      suggestion: {
        char: '/',
        pluginKey: ItemPluginKey,
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith(' ');

          if (overrideSpace) {
            range.to += 1;
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: props,
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run();

          // get reference to `window` object from editor element, to support cross-frame JS usage
          editor.view.dom.ownerDocument.defaultView
            ?.getSelection()
            ?.collapseToEnd();
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes[this.name];
          const allow = !!$from.parent.type.contentMatch.matchType(type);

          return allow;
        },
      },
    };
  },

  group: 'inline',

  inline: true,

  selectable: false,

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: attributes => {
          if (!attributes.id) {
            return {};
          }

          return {
            'data-id': attributes.id,
          };
        },
      },

      label: {
        default: null,
        parseHTML: element => element.getAttribute('data-label'),
        renderHTML: attributes => {
          if (!attributes.label) {
            return {};
          }

          return {
            'data-label': attributes.label,
          };
        },
      },

      icon: {
        default: null,
        parseHTML: element => element.getAttribute('data-icon'),
        renderHTML: attributes => {
          if (!attributes.icon) {
            return {};
          }

          return {
            'data-icon': attributes.icon,
          };
        },
      },

      action: {
        default: null,
        parseHTML: element => element.getAttribute('data-action'),
        renderHTML: attributes => {
          if (!attributes.action) {
            return {};
          }

          return {
            'data-action': attributes.action,
          };
        },
      },

      itemType: {
        default: 'tool',
        parseHTML: element => element.getAttribute('data-item-type'),
        renderHTML: attributes => {
          if (!attributes.itemType) {
            return {};
          }

          return {
            'data-item-type': attributes.itemType,
          };
        },
      },

      attributeModel: {
        default: null,
        parseHTML: element => element.getAttribute('data-attribute-model'),
        renderHTML: attributes => {
          if (!attributes.attributeModel) {
            return {};
          }

          return {
            'data-attribute-model': attributes.attributeModel,
          };
        },
      },

      namespace: {
        default: null,
        parseHTML: element => element.getAttribute('data-namespace'),
        renderHTML: attributes => {
          if (!attributes.namespace) {
            return {};
          }

          return {
            'data-namespace': attributes.namespace,
          };
        },
      },

      visualizationId: {
        default: null,
        parseHTML: element => element.getAttribute('data-visualization-id'),
        renderHTML: attributes => {
          if (!attributes.visualizationId) {
            return {};
          }

          return {
            'data-visualization-id': attributes.visualizationId,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const action = node.attrs.action ? `:${node.attrs.action}` : '';
    const itemType = node.attrs.itemType || 'tool';

    const getItemTypeLabel = type => {
      switch (type) {
        case 'attribute':
        case 'contact_attribute':
        case 'event_attribute':
          return 'Attr';
        case 'library':
          return 'Library';
        case 'visualization':
          return 'UI';
        default:
          return 'Tool';
      }
    };

    // Create icon element if icon is available
    const iconElements = [];
    if (node.attrs.icon && node.attrs.icon.startsWith('i-lucide-')) {
      iconElements.push([
        'span',
        {
          class: `${node.attrs.icon} w-3 h-3 inline-block mr-1`,
        },
      ]);
    }

    // Create the item action content with icon and text
    const itemActionContent = [
      ...iconElements,
      `${node.attrs.label ?? node.attrs.id}${action}`,
    ];

    return [
      'span',
      mergeAttributes(
        {
          'data-type': this.name,
          'data-item-type': itemType,
          class: 'item-reference',
        },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      [
        'span',
        {
          class: 'item-type',
        },
        getItemTypeLabel(itemType),
      ],
      [
        'span',
        {
          class: 'item-action',
        },
        ...itemActionContent,
      ],
    ];
  },

  renderText({ node }) {
    const action = node.attrs.action ? `:${node.attrs.action}` : '';
    return `${node.attrs.label ?? node.attrs.id}${action}`;
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isTool = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isTool = true;
              tr.insertText(
                this.options.deleteTriggerWithBackspace
                  ? ''
                  : this.options.suggestion.char || '',
                pos,
                pos + node.nodeSize
              );

              return false;
            }
            return true;
          });

          return isTool;
        }),
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
